import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import {
  beansService,
  countriesService,
  adminBeansService,
  adminFarmsService,
  adminTestReportsService,
  adminShippingLogisticsService,
  suppliersService
} from '../../../services/supabaseService';
import { supabase } from '../../../lib/supabase';

const CoffeeManagementTab = ({ language }) => {
  const [coffeeRecords, setCoffeeRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [editingRecord, setEditingRecord] = useState(null);
  const [expandedRecord, setExpandedRecord] = useState(null);

  // Reference data
  const [countries, setCountries] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  // Form data for all sections
  const [formData, setFormData] = useState({
    // Bean Information
    nameEn: '',
    nameZh: '',
    countryId: '',
    supplierId: '',
    priceUsdPerKg: '',
    productionYear: new Date()?.getFullYear(),
    processingType: 'washed',
    grade: '',
    moisturePct: '',
    availableQtyKg: '',
    descriptionEn: '',
    descriptionZh: '',
    altitudeM: '',
    isFeatured: false,
    isActive: true,

    // Farm Information
    farmNameEn: '',
    farmNameZh: '',
    farmAreaHa: '',
    farmAltitudeM: '',
    farmLatitude: '',
    farmLongitude: '',
    farmDescriptionEn: '',
    farmDescriptionZh: '',
    shapefile: null,

    // Test Report
    reportNameEn: '',
    reportNameZh: '',
    reportType: 'quality_analysis',
    testDate: '',
    laboratoryName: '',
    reportDescriptionEn: '',
    reportDescriptionZh: '',
    reportFile: null,

    // Shipping Information
    shippingMethod: 'sea_freight',
    portOfLoading: '',
    portOfDischarge: '',
    estimatedTransitDays: '',
    shippingCostUsdPerKg: '',
    minimumOrderKg: '',
    packagingType: 'jute_bags',
    containerType: '40ft',
    incoterms: 'FOB',
    insuranceIncluded: false,
    customsClearanceIncluded: false,
    shippingNotesEn: '',
    shippingNotesZh: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [beansData, countriesData, suppliersData] = await Promise.all([
        beansService?.getAll(),
        countriesService?.getAll(),
        suppliersService?.getAll()
      ]);

      // Load complete coffee records with all related data
      const completeRecords = await Promise.all(
        beansData?.map(async (bean) => {
          // Fetch farm if bean has a farm_id
          let farmData = null;
          if (bean?.farmId) {
            const { data } = await supabase?.from('farms')?.select('*')?.eq('id', bean?.farmId)?.single();
            farmData = data;
          }

          const [testReports, shipping] = await Promise.all([
            supabase?.from('test_reports')?.select('*')?.eq('bean_id', bean?.id),
            supabase?.from('shipping_logistics')?.select('*')?.eq('bean_id', bean?.id)
          ]);

          return {
            ...bean,
            farms: farmData ? [farmData] : [], // Keep as array for compatibility
            testReports: testReports?.data || [],
            shipping: shipping?.data || []
          };
        })
      );

      setCoffeeRecords(completeRecords);
      setCountries(countriesData);
      setSuppliers(suppliersData);
    } catch (err) {
      console.error('Error loading coffee data:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredRecords = coffeeRecords?.filter((record) => {
    const matchesSearch = record?.nameEn?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      record?.nameZh?.includes(searchTerm) ||
      record?.supplier?.nameEn?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      record?.country?.nameEn?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesSearch;
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (field, file) => {
    setFormData(prev => ({ ...prev, [field]: file }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1: // Bean Information
        if (!formData?.nameEn || !formData?.countryId || !formData?.supplierId || !formData?.priceUsdPerKg) {
          alert(language === 'en' ? 'Please fill in all required bean information' : '请填写所有必填的咖啡豆信息');
          return false;
        }
        return true;
      case 2: // Farm Information
        if (!formData?.farmNameEn || !formData?.farmAreaHa) {
          alert(language === 'en' ? 'Please fill in farm name and area' : '请填写农场名称和面积');
          return false;
        }
        return true;
      case 3: // Test Report
        return true; // Optional
      case 4: // Shipping
        return true; // Optional
      default:
        return true;
    }
  };

  const handleNext = () => {
    // Allow navigation to next step without validation for demo purposes
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const isEditing = editingRecord !== null;

      // Step 1: Create or Update Bean
      const beanData = {
        nameEn: formData?.nameEn,
        nameZh: formData?.nameZh,
        countryId: formData?.countryId,
        supplierId: formData?.supplierId,
        priceUsdPerKg: parseFloat(formData?.priceUsdPerKg),
        productionYear: parseInt(formData?.productionYear),
        processingType: formData?.processingType,
        grade: formData?.grade,
        moisturePct: formData?.moisturePct ? parseFloat(formData?.moisturePct) : null,
        availableQtyKg: formData?.availableQtyKg ? parseFloat(formData?.availableQtyKg) : null,
        descriptionEn: formData?.descriptionEn,
        descriptionZh: formData?.descriptionZh,
        altitudeM: formData?.altitudeM ? parseInt(formData?.altitudeM) : null,
        isFeatured: formData?.isFeatured,
        isActive: formData?.isActive
      };

      let beanId;
      if (isEditing) {
        await adminBeansService?.update(editingRecord?.id, beanData);
        beanId = editingRecord?.id;
      } else {
        const createdBean = await adminBeansService?.create(beanData);
        beanId = createdBean?.id;
      }

      // Step 2: Create or Update Farm
      if (formData?.farmNameEn) {
        const farmData = {
          nameEn: formData?.farmNameEn,
          nameZh: formData?.farmNameZh,
          countryId: formData?.countryId,
          supplierId: formData?.supplierId,
          beanId: beanId,
          areaHa: parseFloat(formData?.farmAreaHa),
          altitudeM: formData?.farmAltitudeM ? parseInt(formData?.farmAltitudeM) : null,
          latitude: formData?.farmLatitude ? parseFloat(formData?.farmLatitude) : null,
          longitude: formData?.farmLongitude ? parseFloat(formData?.farmLongitude) : null,
          descriptionEn: formData?.farmDescriptionEn,
          descriptionZh: formData?.farmDescriptionZh
        };

        if (isEditing && editingRecord?.farms?.[0]?.id) {
          await adminFarmsService?.update(editingRecord?.farms?.[0]?.id, farmData);
        } else {
          await adminFarmsService?.create(farmData);
        }

        // Upload shapefile if provided
        if (formData?.shapefile) {
          await adminFarmsService?.uploadShapefile(formData?.shapefile, beanId);
        }
      }

      // Step 3: Create or Update Test Report
      if (formData?.reportFile || (isEditing && formData?.reportNameEn)) {
        let fileUrl = editingRecord?.testReports?.[0]?.fileUrl;

        if (formData?.reportFile) {
          const { url } = await adminTestReportsService?.uploadReport(formData?.reportFile, beanId);
          fileUrl = url;
        }

        const reportData = {
          beanId: beanId,
          reportNameEn: formData?.reportNameEn,
          reportNameZh: formData?.reportNameZh,
          fileUrl: fileUrl,
          fileType: formData?.reportFile?.name?.split('.')?.pop() || editingRecord?.testReports?.[0]?.fileType,
          fileSizeBytes: formData?.reportFile?.size || editingRecord?.testReports?.[0]?.fileSizeBytes,
          reportType: formData?.reportType,
          testDate: formData?.testDate || null,
          laboratoryName: formData?.laboratoryName || null,
          descriptionEn: formData?.reportDescriptionEn || null,
          descriptionZh: formData?.reportDescriptionZh || null
        };

        if (isEditing && editingRecord?.testReports?.[0]?.id) {
          await adminTestReportsService?.update(editingRecord?.testReports?.[0]?.id, reportData);
        } else if (fileUrl) {
          await adminTestReportsService?.create(reportData);
        }
      }

      // Step 4: Create or Update Shipping Logistics
      if (formData?.portOfLoading && formData?.portOfDischarge) {
        const shippingData = {
          beanId: beanId,
          supplierId: formData?.supplierId,
          shippingMethod: formData?.shippingMethod,
          portOfLoading: formData?.portOfLoading,
          portOfDischarge: formData?.portOfDischarge,
          estimatedTransitDays: formData?.estimatedTransitDays ? parseInt(formData?.estimatedTransitDays) : null,
          shippingCostUsdPerKg: formData?.shippingCostUsdPerKg ? parseFloat(formData?.shippingCostUsdPerKg) : null,
          minimumOrderKg: formData?.minimumOrderKg ? parseFloat(formData?.minimumOrderKg) : null,
          packagingType: formData?.packagingType,
          containerType: formData?.containerType,
          incoterms: formData?.incoterms,
          insuranceIncluded: formData?.insuranceIncluded,
          customsClearanceIncluded: formData?.customsClearanceIncluded,
          notesEn: formData?.shippingNotesEn,
          notesZh: formData?.shippingNotesZh
        };

        if (isEditing && editingRecord?.shipping?.[0]?.id) {
          await adminShippingLogisticsService?.update(editingRecord?.shipping?.[0]?.id, shippingData);
        } else {
          await adminShippingLogisticsService?.create(shippingData);
        }
      }

      await loadData();
      setShowAddModal(false);
      setCurrentStep(1);
      setEditingRecord(null);
      resetForm();

      alert(language === 'en'
        ? (isEditing ? 'Coffee record updated successfully!' : 'Coffee record created successfully!')
        : (isEditing ? '咖啡记录更新成功！' : '咖啡记录创建成功！'));
    } catch (error) {
      console.error('Failed to save coffee record:', error);
      alert(language === 'en' ? `Failed to save record: ${error?.message}` : `保存记录失败: ${error?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nameEn: '',
      nameZh: '',
      countryId: '',
      supplierId: '',
      priceUsdPerKg: '',
      productionYear: new Date()?.getFullYear(),
      processingType: 'washed',
      grade: '',
      moisturePct: '',
      availableQtyKg: '',
      descriptionEn: '',
      descriptionZh: '',
      altitudeM: '',
      isFeatured: false,
      isActive: true,
      farmNameEn: '',
      farmNameZh: '',
      farmAreaHa: '',
      farmAltitudeM: '',
      farmLatitude: '',
      farmLongitude: '',
      farmDescriptionEn: '',
      farmDescriptionZh: '',
      shapefile: null,
      reportNameEn: '',
      reportNameZh: '',
      reportType: 'quality_analysis',
      testDate: '',
      laboratoryName: '',
      reportDescriptionEn: '',
      reportDescriptionZh: '',
      reportFile: null,
      shippingMethod: 'sea_freight',
      portOfLoading: '',
      portOfDischarge: '',
      estimatedTransitDays: '',
      shippingCostUsdPerKg: '',
      minimumOrderKg: '',
      packagingType: 'jute_bags',
      containerType: '40ft',
      incoterms: 'FOB',
      insuranceIncluded: false,
      customsClearanceIncluded: false,
      shippingNotesEn: '',
      shippingNotesZh: ''
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm(language === 'en' ? 'Delete this coffee record and all related data?' : '删除此咖啡记录及所有相关数据？')) {
      try {
        setLoading(true);
        await adminBeansService?.delete(id);
        await loadData();
        alert(language === 'en' ? 'Coffee record deleted successfully!' : '咖啡记录删除成功！');
      } catch (error) {
        console.error('Delete error:', error);
        alert(language === 'en'
          ? `Failed to delete: ${error?.message || 'Unknown error'}`
          : `删除失败: ${error?.message || '未知错误'}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = async (record) => {
    setEditingRecord(record);

    // Populate form with existing data
    setFormData({
      // Bean Information
      nameEn: record?.nameEn || '',
      nameZh: record?.nameZh || '',
      countryId: record?.countryId || '',
      supplierId: record?.supplierId || '',
      priceUsdPerKg: record?.priceUsdPerKg || '',
      productionYear: record?.productionYear || new Date()?.getFullYear(),
      processingType: record?.processingType || 'washed',
      grade: record?.grade || '',
      moisturePct: record?.moisturePct || '',
      availableQtyKg: record?.availableQtyKg || '',
      descriptionEn: record?.descriptionEn || '',
      descriptionZh: record?.descriptionZh || '',
      altitudeM: record?.altitudeM || '',
      isFeatured: record?.isFeatured || false,
      isActive: record?.isActive !== undefined ? record?.isActive : true,

      // Farm Information (from first farm if exists)
      farmNameEn: record?.farms?.[0]?.nameEn || '',
      farmNameZh: record?.farms?.[0]?.nameZh || '',
      farmAreaHa: record?.farms?.[0]?.areaHa || '',
      farmAltitudeM: record?.farms?.[0]?.altitudeM || '',
      farmLatitude: record?.farms?.[0]?.latitude || '',
      farmLongitude: record?.farms?.[0]?.longitude || '',
      farmDescriptionEn: record?.farms?.[0]?.descriptionEn || '',
      farmDescriptionZh: record?.farms?.[0]?.descriptionZh || '',
      shapefile: null,

      // Test Report (from first report if exists)
      reportNameEn: record?.testReports?.[0]?.reportNameEn || '',
      reportNameZh: record?.testReports?.[0]?.reportNameZh || '',
      reportType: record?.testReports?.[0]?.reportType || 'quality_analysis',
      testDate: record?.testReports?.[0]?.testDate || '',
      laboratoryName: record?.testReports?.[0]?.laboratoryName || '',
      reportDescriptionEn: record?.testReports?.[0]?.descriptionEn || '',
      reportDescriptionZh: record?.testReports?.[0]?.descriptionZh || '',
      reportFile: null,

      // Shipping Information (from first shipping if exists)
      shippingMethod: record?.shipping?.[0]?.shippingMethod || 'sea_freight',
      portOfLoading: record?.shipping?.[0]?.portOfLoading || '',
      portOfDischarge: record?.shipping?.[0]?.portOfDischarge || '',
      estimatedTransitDays: record?.shipping?.[0]?.estimatedTransitDays || '',
      shippingCostUsdPerKg: record?.shipping?.[0]?.shippingCostUsdPerKg || '',
      minimumOrderKg: record?.shipping?.[0]?.minimumOrderKg || '',
      packagingType: record?.shipping?.[0]?.packagingType || 'jute_bags',
      containerType: record?.shipping?.[0]?.containerType || '40ft',
      incoterms: record?.shipping?.[0]?.incoterms || 'FOB',
      insuranceIncluded: record?.shipping?.[0]?.insuranceIncluded || false,
      customsClearanceIncluded: record?.shipping?.[0]?.customsClearanceIncluded || false,
      shippingNotesEn: record?.shipping?.[0]?.notesEn || '',
      shippingNotesZh: record?.shipping?.[0]?.notesZh || ''
    });

    setShowAddModal(true);
    setCurrentStep(1);
  };


  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              {language === 'en' ? 'Step 1: Bean Information' : '步骤 1: 咖啡豆信息'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={language === 'en' ? 'Bean Name (English) *' : '咖啡豆名称（英文）*'}
                value={formData?.nameEn}
                onChange={(e) => handleInputChange('nameEn', e?.target?.value)}
                required
              />
              <Input
                label={language === 'en' ? 'Bean Name (Chinese)' : '咖啡豆名称（中文）'}
                value={formData?.nameZh}
                onChange={(e) => handleInputChange('nameZh', e?.target?.value)}
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'en' ? 'Country *' : '国家 *'}
                </label>
                <select
                  value={formData?.countryId}
                  onChange={(e) => handleInputChange('countryId', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  required
                >
                  <option value="">{language === 'en' ? 'Select Country' : '选择国家'}</option>
                  {countries?.map(country => (
                    <option key={country?.id} value={country?.id}>
                      {language === 'en' ? country?.nameEn : country?.nameZh}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'en' ? 'Supplier *' : '供应商 *'}
                </label>
                <select
                  value={formData?.supplierId}
                  onChange={(e) => handleInputChange('supplierId', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  required
                >
                  <option value="">{language === 'en' ? 'Select Supplier' : '选择供应商'}</option>
                  {suppliers?.map(supplier => (
                    <option key={supplier?.id} value={supplier?.id}>
                      {language === 'en' ? supplier?.nameEn : supplier?.nameZh}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                type="number"
                label={language === 'en' ? 'Price (USD/kg) *' : '价格（美元/公斤）*'}
                value={formData?.priceUsdPerKg}
                onChange={(e) => handleInputChange('priceUsdPerKg', e?.target?.value)}
                required
              />
              <Input
                type="number"
                label={language === 'en' ? 'Production Year' : '生产年份'}
                value={formData?.productionYear}
                onChange={(e) => handleInputChange('productionYear', e?.target?.value)}
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'en' ? 'Processing Type' : '处理方式'}
                </label>
                <select
                  value={formData?.processingType}
                  onChange={(e) => handleInputChange('processingType', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="washed">{language === 'en' ? 'Washed' : '水洗'}</option>
                  <option value="natural">{language === 'en' ? 'Natural' : '日晒'}</option>
                  <option value="honey">{language === 'en' ? 'Honey' : '蜜处理'}</option>
                  <option value="pulped_natural">{language === 'en' ? 'Pulped Natural' : '去果皮日晒'}</option>
                </select>
              </div>
              <Input
                label={language === 'en' ? 'Grade' : '等级'}
                value={formData?.grade}
                onChange={(e) => handleInputChange('grade', e?.target?.value)}
              />
              <Input
                type="number"
                label={language === 'en' ? 'Moisture (%)' : '水分含量（%）'}
                value={formData?.moisturePct}
                onChange={(e) => handleInputChange('moisturePct', e?.target?.value)}
              />
              <Input
                type="number"
                label={language === 'en' ? 'Available Quantity (kg)' : '可用数量（公斤）'}
                value={formData?.availableQtyKg}
                onChange={(e) => handleInputChange('availableQtyKg', e?.target?.value)}
              />
              <Input
                type="number"
                label={language === 'en' ? 'Altitude (m)' : '海拔（米）'}
                value={formData?.altitudeM}
                onChange={(e) => handleInputChange('altitudeM', e?.target?.value)}
              />
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'en' ? 'Description (English)' : '描述（英文）'}
                </label>
                <textarea
                  value={formData?.descriptionEn}
                  onChange={(e) => handleInputChange('descriptionEn', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'en' ? 'Description (Chinese)' : '描述（中文）'}
                </label>
                <textarea
                  value={formData?.descriptionZh}
                  onChange={(e) => handleInputChange('descriptionZh', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  rows={3}
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData?.isFeatured}
                  onChange={(e) => handleInputChange('isFeatured', e?.target?.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-foreground">{language === 'en' ? 'Featured' : '精选'}</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData?.isActive}
                  onChange={(e) => handleInputChange('isActive', e?.target?.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-foreground">{language === 'en' ? 'Active' : '激活'}</span>
              </label>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              {language === 'en' ? 'Step 2: Farm Information' : '步骤 2: 农场信息'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={language === 'en' ? 'Farm Name (English) *' : '农场名称（英文）*'}
                value={formData?.farmNameEn}
                onChange={(e) => handleInputChange('farmNameEn', e?.target?.value)}
                required
              />
              <Input
                label={language === 'en' ? 'Farm Name (Chinese)' : '农场名称（中文）'}
                value={formData?.farmNameZh}
                onChange={(e) => handleInputChange('farmNameZh', e?.target?.value)}
              />
              <Input
                type="number"
                label={language === 'en' ? 'Farm Area (hectares) *' : '农场面积（公顷）*'}
                value={formData?.farmAreaHa}
                onChange={(e) => handleInputChange('farmAreaHa', e?.target?.value)}
                required
              />
              <Input
                type="number"
                label={language === 'en' ? 'Farm Altitude (m)' : '农场海拔（米）'}
                value={formData?.farmAltitudeM}
                onChange={(e) => handleInputChange('farmAltitudeM', e?.target?.value)}
              />
              <Input
                type="number"
                label={language === 'en' ? 'Latitude' : '纬度'}
                value={formData?.farmLatitude}
                onChange={(e) => handleInputChange('farmLatitude', e?.target?.value)}
                placeholder="e.g., -1.2345"
              />
              <Input
                type="number"
                label={language === 'en' ? 'Longitude' : '经度'}
                value={formData?.farmLongitude}
                onChange={(e) => handleInputChange('farmLongitude', e?.target?.value)}
                placeholder="e.g., 36.7890"
              />
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'en' ? 'Farm Description (English)' : '农场描述（英文）'}
                </label>
                <textarea
                  value={formData?.farmDescriptionEn}
                  onChange={(e) => handleInputChange('farmDescriptionEn', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'en' ? 'Farm Description (Chinese)' : '农场描述（中文）'}
                </label>
                <textarea
                  value={formData?.farmDescriptionZh}
                  onChange={(e) => handleInputChange('farmDescriptionZh', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'en' ? 'Shapefile (Optional)' : '形状文件（可选）'}
                </label>
                <input
                  type="file"
                  accept=".shp,.zip"
                  onChange={(e) => handleFileChange('shapefile', e?.target?.files?.[0])}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {language === 'en' ? 'Upload shapefile for farm boundaries' : '上传农场边界形状文件'}
                </p>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              {language === 'en' ? 'Step 3: Test Report (Optional)' : '步骤 3: 测试报告（可选）'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={language === 'en' ? 'Report Name (English)' : '报告名称（英文）'}
                value={formData?.reportNameEn}
                onChange={(e) => handleInputChange('reportNameEn', e?.target?.value)}
              />
              <Input
                label={language === 'en' ? 'Report Name (Chinese)' : '报告名称（中文）'}
                value={formData?.reportNameZh}
                onChange={(e) => handleInputChange('reportNameZh', e?.target?.value)}
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'en' ? 'Report Type' : '报告类型'}
                </label>
                <select
                  value={formData?.reportType}
                  onChange={(e) => handleInputChange('reportType', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="quality_analysis">{language === 'en' ? 'Quality Analysis' : '质量分析'}</option>
                  <option value="cupping_score">{language === 'en' ? 'Cupping Score' : '杯测评分'}</option>
                  <option value="certification">{language === 'en' ? 'Certification' : '认证'}</option>
                  <option value="lab_test">{language === 'en' ? 'Lab Test' : '实验室测试'}</option>
                </select>
              </div>
              <Input
                type="date"
                label={language === 'en' ? 'Test Date' : '测试日期'}
                value={formData?.testDate}
                onChange={(e) => handleInputChange('testDate', e?.target?.value)}
              />
              <Input
                label={language === 'en' ? 'Laboratory Name' : '实验室名称'}
                value={formData?.laboratoryName}
                onChange={(e) => handleInputChange('laboratoryName', e?.target?.value)}
              />
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'en' ? 'Report Description (English)' : '报告描述（英文）'}
                </label>
                <textarea
                  value={formData?.reportDescriptionEn}
                  onChange={(e) => handleInputChange('reportDescriptionEn', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'en' ? 'Report Description (Chinese)' : '报告描述（中文）'}
                </label>
                <textarea
                  value={formData?.reportDescriptionZh}
                  onChange={(e) => handleInputChange('reportDescriptionZh', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'en' ? 'Upload Report File' : '上传报告文件'}
                </label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileChange('reportFile', e?.target?.files?.[0])}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              {language === 'en' ? 'Step 4: Shipping Information (Optional)' : '步骤 4: 运输信息（可选）'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'en' ? 'Shipping Method' : '运输方式'}
                </label>
                <select
                  value={formData?.shippingMethod}
                  onChange={(e) => handleInputChange('shippingMethod', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="sea_freight">{language === 'en' ? 'Sea Freight' : '海运'}</option>
                  <option value="air_freight">{language === 'en' ? 'Air Freight' : '空运'}</option>
                  <option value="land_transport">{language === 'en' ? 'Land Transport' : '陆运'}</option>
                </select>
              </div>
              <Input
                label={language === 'en' ? 'Port of Loading' : '装货港'}
                value={formData?.portOfLoading}
                onChange={(e) => handleInputChange('portOfLoading', e?.target?.value)}
              />
              <Input
                label={language === 'en' ? 'Port of Discharge' : '卸货港'}
                value={formData?.portOfDischarge}
                onChange={(e) => handleInputChange('portOfDischarge', e?.target?.value)}
              />
              <Input
                type="number"
                label={language === 'en' ? 'Estimated Transit Days' : '预计运输天数'}
                value={formData?.estimatedTransitDays}
                onChange={(e) => handleInputChange('estimatedTransitDays', e?.target?.value)}
              />
              <Input
                type="number"
                label={language === 'en' ? 'Shipping Cost (USD/kg)' : '运输成本（美元/公斤）'}
                value={formData?.shippingCostUsdPerKg}
                onChange={(e) => handleInputChange('shippingCostUsdPerKg', e?.target?.value)}
              />
              <Input
                type="number"
                label={language === 'en' ? 'Minimum Order (kg)' : '最小订单（公斤）'}
                value={formData?.minimumOrderKg}
                onChange={(e) => handleInputChange('minimumOrderKg', e?.target?.value)}
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'en' ? 'Packaging Type' : '包装类型'}
                </label>
                <select
                  value={formData?.packagingType}
                  onChange={(e) => handleInputChange('packagingType', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="jute_bags">{language === 'en' ? 'Jute Bags' : '麻袋'}</option>
                  <option value="grain_pro">{language === 'en' ? 'GrainPro' : 'GrainPro袋'}</option>
                  <option value="vacuum_sealed">{language === 'en' ? 'Vacuum Sealed' : '真空密封'}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'en' ? 'Container Type' : '集装箱类型'}
                </label>
                <select
                  value={formData?.containerType}
                  onChange={(e) => handleInputChange('containerType', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="20ft">20ft</option>
                  <option value="40ft">40ft</option>
                  <option value="40ft_hc">40ft HC</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'en' ? 'Incoterms' : '国际贸易术语'}
                </label>
                <select
                  value={formData?.incoterms}
                  onChange={(e) => handleInputChange('incoterms', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="FOB">FOB</option>
                  <option value="CIF">CIF</option>
                  <option value="EXW">EXW</option>
                  <option value="DDP">DDP</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData?.insuranceIncluded}
                  onChange={(e) => handleInputChange('insuranceIncluded', e?.target?.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-foreground">{language === 'en' ? 'Insurance Included' : '包含保险'}</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData?.customsClearanceIncluded}
                  onChange={(e) => handleInputChange('customsClearanceIncluded', e?.target?.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-foreground">{language === 'en' ? 'Customs Clearance Included' : '包含清关'}</span>
              </label>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'en' ? 'Shipping Notes (English)' : '运输备注（英文）'}
                </label>
                <textarea
                  value={formData?.shippingNotesEn}
                  onChange={(e) => handleInputChange('shippingNotesEn', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'en' ? 'Shipping Notes (Chinese)' : '运输备注（中文）'}
                </label>
                <textarea
                  value={formData?.shippingNotesZh}
                  onChange={(e) => handleInputChange('shippingNotesZh', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  rows={2}
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading && !showAddModal) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            {language === 'en' ? 'Coffee Management' : '咖啡管理'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {language === 'en' ? 'Manage complete coffee records with beans, farms, test reports, and shipping' : '管理完整的咖啡记录，包括咖啡豆、农场、测试报告和运输'}
          </p>
        </div>
        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setShowAddModal(true)}
        >
          {language === 'en' ? 'Add Coffee Record' : '添加咖啡记录'}
        </Button>
      </div>

      <Input
        type="search"
        placeholder={language === 'en' ? 'Search coffee records...' : '搜索咖啡记录...'}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e?.target?.value)}
      />

      <div className="grid grid-cols-1 gap-4">
        {filteredRecords?.map((record) => (
          <div key={record?.id} className="bg-card border border-border rounded-lg p-5">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-4">
                {record?.imageUrl && (
                  <Image
                    src={record?.imageUrl}
                    alt={record?.nameEn}
                    className="w-20 h-20 object-cover rounded-md"
                  />
                )}
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    {language === 'en' ? record?.nameEn : record?.nameZh || record?.nameEn}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-muted-foreground">
                      {language === 'en' ? record?.country?.nameEn : record?.country?.nameZh}
                    </span>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-sm text-muted-foreground">
                      {language === 'en' ? record?.supplier?.nameEn : record?.supplier?.nameZh}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    {record?.isActive && (
                      <span className="px-2 py-0.5 bg-success/10 text-success text-xs rounded-full">
                        {language === 'en' ? 'Active' : '激活'}
                      </span>
                    )}
                    {record?.isFeatured && (
                      <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                        {language === 'en' ? 'Featured' : '精选'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName={expandedRecord === record?.id ? 'ChevronUp' : 'ChevronDown'}
                  onClick={() => setExpandedRecord(expandedRecord === record?.id ? null : record?.id)}
                >
                  {language === 'en' ? 'Details' : '详情'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Edit"
                  onClick={() => handleEdit(record)}
                >
                  {language === 'en' ? 'Edit' : '编辑'}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  iconName="Trash2"
                  onClick={() => handleDelete(record?.id)}
                />
              </div>
            </div>

            {expandedRecord === record?.id && (
              <div className="border-t border-border pt-4 mt-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Sprout" size={18} color="var(--color-primary)" />
                      <h4 className="font-semibold text-foreground">
                        {language === 'en' ? 'Farms' : '农场'}
                      </h4>
                    </div>
                    <p className="text-2xl font-bold text-foreground">{record?.farms?.length || 0}</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="FileText" size={18} color="var(--color-primary)" />
                      <h4 className="font-semibold text-foreground">
                        {language === 'en' ? 'Test Reports' : '测试报告'}
                      </h4>
                    </div>
                    <p className="text-2xl font-bold text-foreground">{record?.testReports?.length || 0}</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Ship" size={18} color="var(--color-primary)" />
                      <h4 className="font-semibold text-foreground">
                        {language === 'en' ? 'Shipping Options' : '运输选项'}
                      </h4>
                    </div>
                    <p className="text-2xl font-bold text-foreground">{record?.shipping?.length || 0}</p>
                  </div>
                </div>

                {record?.farms?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">
                      {language === 'en' ? 'Farm Details' : '农场详情'}
                    </h4>
                    {record?.farms?.map((farm) => (
                      <div key={farm?.id} className="bg-muted/30 rounded-md p-3 mb-2">
                        <p className="font-medium text-foreground">
                          {language === 'en' ? farm?.nameEn : farm?.nameZh || farm?.nameEn}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {language === 'en' ? 'Area:' : '面积:'} {farm?.areaHa} ha
                          {farm?.altitudeM && ` • ${language === 'en' ? 'Altitude:' : '海拔:'} ${farm?.altitudeM}m`}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredRecords?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Coffee" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-muted-foreground">{language === 'en' ? 'No coffee records found' : '未找到咖啡记录'}</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-foreground">
                {editingRecord
                  ? (language === 'en' ? 'Edit Coffee Record' : '编辑咖啡记录')
                  : (language === 'en' ? 'Add Coffee Record' : '添加咖啡记录')}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setCurrentStep(1);
                  setEditingRecord(null);
                  resetForm();
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            {/* Step Indicator */}
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                {[1, 2, 3, 4]?.map((step) => (
                  <div key={step} className="flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === step
                        ? 'bg-primary text-white'
                        : currentStep > step
                          ? 'bg-success text-white' : 'bg-muted text-muted-foreground'
                        }`}
                    >
                      {currentStep > step ? <Icon name="Check" size={16} /> : step}
                    </div>
                    {step < 4 && (
                      <div
                        className={`w-16 h-1 mx-2 ${currentStep > step ? 'bg-success' : 'bg-muted'
                          }`}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-xs text-muted-foreground">{language === 'en' ? 'Bean' : '咖啡豆'}</span>
                <span className="text-xs text-muted-foreground">{language === 'en' ? 'Farm' : '农场'}</span>
                <span className="text-xs text-muted-foreground">{language === 'en' ? 'Test' : '测试'}</span>
                <span className="text-xs text-muted-foreground">{language === 'en' ? 'Shipping' : '运输'}</span>
              </div>
            </div>

            <div className="p-6">
              {renderStepContent()}
            </div>

            <div className="sticky bottom-0 bg-card border-t border-border p-4 flex items-center justify-between">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 1}
              >
                {language === 'en' ? 'Previous' : '上一步'}
              </Button>
              <div className="flex items-center gap-2">
                {currentStep < 4 ? (
                  <Button variant="default" onClick={handleNext}>
                    {language === 'en' ? 'Next' : '下一步'}
                  </Button>
                ) : (
                  <Button variant="default" onClick={handleSubmit} disabled={loading}>
                    {loading ? (
                      <Icon name="Loader2" size={16} className="animate-spin" />
                    ) : (
                      editingRecord
                        ? (language === 'en' ? 'Update Record' : '更新记录')
                        : (language === 'en' ? 'Create Record' : '创建记录')
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoffeeManagementTab;